import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import firebase from "firebase/app"
import "firebase/firestore"

import { FirebaseContext } from "../idealline/Firebase"
import {
  SPONSORSHIP_PACKAGES,
  buildSponsorshipRequest,
  sponsorshipRequestId,
  validateSponsorshipRequest,
} from "../../utils/sponsorshipRequest"

const emptyForm = {
  contactName: "",
  companyName: "",
  role: "",
  businessEmail: "",
  businessWebsite: "",
  campaignSummary: "",
}

const Field = ({ error, label, name, onChange, required = false, type = "text", value }) => (
  <label className="sponsor-field">
    <span>{label}{required ? " *" : ""}</span>
    <input
      aria-describedby={error ? `${name}-error` : undefined}
      aria-invalid={Boolean(error)}
      autoComplete={name === "businessEmail" ? "email" : undefined}
      name={name}
      onChange={onChange}
      required={required}
      type={type}
      value={value}
    />
    {error && <small id={`${name}-error`} role="alert">{error}</small>}
  </label>
)

const SponsorshipRequestForm = ({ onActionReady, placement }) => {
  const { app: firebaseApp, user } = useContext(FirebaseContext)
  const [form, setForm] = useState(emptyForm)
  const [errors, setErrors] = useState({})
  const [state, setState] = useState("idle")
  const [existing, setExisting] = useState(null)
  const sponsorshipPackage = SPONSORSHIP_PACKAGES[placement.packageKey]
  const userUid = user?.uid || ""
  const userEmail = user?.email || ""
  const userDisplayName = user?.displayName || ""

  const requestId = useMemo(
    () => sponsorshipRequestId(userUid),
    [userUid],
  )
  const identityKey = `${userUid}:${requestId}`
  const identityKeyRef = useRef(identityKey)
  const mounted = useRef(true)
  identityKeyRef.current = identityKey

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  useEffect(() => {
    if (user !== null && !existing) onActionReady?.()
  }, [existing, onActionReady, user])

  useEffect(() => {
    let active = true
    setExisting(null)
    setErrors({})
    setState("idle")
    setForm({
      ...emptyForm,
      contactName: userDisplayName,
      businessEmail: userEmail,
    })
    if (!userUid || !firebaseApp || !requestId) return () => { active = false }
    firebaseApp.firestore().collection("sponsorshipRequests").doc(requestId).get()
      .then(snapshot => {
        if (active) setExisting(snapshot.exists ? { id: snapshot.id, ...snapshot.data() } : null)
      })
      .catch(() => {})
    return () => { active = false }
  }, [firebaseApp, requestId, userDisplayName, userEmail, userUid])

  const input = {
    ...form,
    ...placement,
    uid: user?.uid || "",
    accountEmail: user?.email || "",
  }

  const handleChange = event => {
    const { name, value } = event.target
    setForm(current => ({ ...current, [name]: value }))
    setErrors(current => ({ ...current, [name]: undefined, form: undefined }))
  }

  const handleSubmit = async event => {
    event.preventDefault()
    const nextErrors = validateSponsorshipRequest(input)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length || !firebaseApp || !requestId) return

    const submissionIdentity = identityKey
    setState("submitting")
    try {
      const serverTimestamp = firebase.firestore.FieldValue.serverTimestamp()
      const request = buildSponsorshipRequest(input, serverTimestamp)
      await firebaseApp.firestore().collection("sponsorshipRequests").doc(requestId).set(request)
      if (!mounted.current || identityKeyRef.current !== submissionIdentity) return
      setExisting({ id: requestId, ...request })
      setState("submitted")
    } catch (error) {
      if (!mounted.current || identityKeyRef.current !== submissionIdentity) return
      setState("error")
      setErrors(current => ({
        ...current,
        form: error?.code === "permission-denied"
          ? "This request already exists or could not be authorized."
          : "We could not send your request. Please try again.",
      }))
    }
  }

  if (user === null) {
    return (
      <section className="sponsor-form sponsor-form--auth" aria-live="polite">
        <p className="sponsor-kicker">Request access</p>
        <h2>Checking your Open Racer account</h2>
        <p>Loading your secure sponsorship request status.</p>
      </section>
    )
  }

  if (!user) {
    const redirect = encodeURIComponent(typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}${window.location.hash}` : "/sponsorship#sponsorship-request")
    return (
      <section className="sponsor-form sponsor-form--auth" aria-labelledby="sponsor-sign-in-title">
        <p className="sponsor-kicker">Request access</p>
        <h2 id="sponsor-sign-in-title">Sign in to request sponsorship</h2>
        <p className="sponsor-form__selection"><strong>Selected package:</strong> {sponsorshipPackage.name} for {placement.targetName}</p>
        <p>We require an Open Racer account so every request has a verified owner and a private status record.</p>
        <a className="sponsor-button" href={`/profile?redirect=${redirect}`}>Sign in to continue</a>
      </section>
    )
  }

  if (existing) {
    const status = existing.status || "pending"
    return (
      <section className="sponsor-form sponsor-form--complete" aria-live="polite" role="status">
        <p className="sponsor-kicker">Request received</p>
        <h2>{status === "pending" ? "Your request was submitted" : `Request ${status}`}</h2>
        <p>We saved your {existing.packageName || sponsorshipPackage.name} request for {existing.targetName || placement.targetName}. We will contact you at {existing.businessEmail || user.email} after review.</p>
        <p className="sponsor-fine-print">No payment is taken and no sponsorship is activated by this request.</p>
      </section>
    )
  }

  return (
    <form className="sponsor-form" onSubmit={handleSubmit} noValidate>
      <header>
        <p className="sponsor-kicker">Request sponsorship</p>
        <h2>Tell us who should own this partnership</h2>
        <p>{sponsorshipPackage.name} for <strong>{placement.targetName}</strong> at €{sponsorshipPackage.monthlyPriceEur} per month after approval.</p>
      </header>

      {errors.form && <div className="sponsor-form-error" role="alert">{errors.form}</div>}

      <div className="sponsor-form-grid">
        <Field error={errors.contactName} label="Full name" name="contactName" onChange={handleChange} required value={form.contactName} />
        <Field error={errors.companyName} label="Company" name="companyName" onChange={handleChange} required value={form.companyName} />
        <Field error={errors.role} label="Your role" name="role" onChange={handleChange} required value={form.role} />
        <Field error={errors.businessEmail} label="Business email" name="businessEmail" onChange={handleChange} required type="email" value={form.businessEmail} />
        <Field error={errors.businessWebsite} label="Website" name="businessWebsite" onChange={handleChange} type="url" value={form.businessWebsite} />
      </div>

      <label className="sponsor-field sponsor-field--wide">
        <span>What would you like to promote?</span>
        <textarea
          aria-describedby={errors.campaignSummary ? "campaignSummary-error" : undefined}
          aria-invalid={Boolean(errors.campaignSummary)}
          maxLength="2000"
          name="campaignSummary"
          onChange={handleChange}
          placeholder="Your goals, audience, launch timing, or proposed campaign."
          rows="5"
          value={form.campaignSummary}
        />
        <small>{form.campaignSummary.length}/2,000</small>
        {errors.campaignSummary && <small id="campaignSummary-error" role="alert">{errors.campaignSummary}</small>}
      </label>

      <div className="sponsor-submit-row">
        <button className="sponsor-button" disabled={state === "submitting"} type="submit">
          {state === "submitting" ? "Sending request..." : "Request sponsorship"}
        </button>
        <p>No payment is taken. Approval and subscription setup happen separately.</p>
      </div>
    </form>
  )
}

export default SponsorshipRequestForm
